import React, { useCallback, useEffect, useMemo, useState } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "./DeviceConfigurationModal.module.css";
import modalStyles from "../EditAppointmentModal/EditAppointmentModal.module.css";
import { AppointmentDevice, DeviceCategory, DeviceConfig, StaffDevice } from "../../../../services/types/device.types";
import DeviceConfigForm from "../../../../components/forms/deviceConfigForm/DeviceConfigForm";
import { DeviceConfigSections } from "../../../../components/modals/treatmentProtocolDetails/deviceConfigDetails";
import {
  DEVICE_CATEGORIES,
  DEVICE_CATEGORY_DISPLAY,
  getDefaultDeviceConfig,
  getDeviceCapabilities,
  normalizeDeviceConfig,
  staffDeviceToAppointmentDevice,
  validateDeviceConfig,
} from "../../../../utils/deviceConfiguration";

interface DeviceConfigurationModalProps {
  devices: StaffDevice[];
  initialDevice?: AppointmentDevice | null;
  /**
   * The patient's assigned device protocol. When present it dictates the session — modality,
   * montage and length are shown here for confirmation and cannot be edited.
   */
  protocolTemplate?: DeviceConfig | null;
  /**
   * True when the appointment has a patient, which is what makes a device protocol mandatory.
   * Without one there is nothing to configure and the modal says so.
   */
  protocolRequired?: boolean;
  onConfirm: (device: AppointmentDevice) => void;
  onCancel: () => void;
}

/**
 * With a device protocol assigned, the only decision left here is which machine delivers it: the
 * protocol fixes the modality and the montage, and the list is narrowed to the devices that
 * support it and are free in the window. Everything clinical is a read-only summary, because the
 * treatment is decided in the patient's file and not per appointment.
 *
 * Without a patient — a machine booked on its own — there is no protocol to impose, so the older
 * free-form path stays: pick a modality, and the device list follows it.
 */
function DeviceConfigurationModal({
  devices,
  initialDevice,
  protocolTemplate,
  protocolRequired = false,
  onConfirm,
  onCancel,
}: DeviceConfigurationModalProps) {
  const isRtl = i18n.language === "he";

  /** The protocol binds: no modality choice, no montage editing. */
  const isProtocolDriven = Boolean(protocolTemplate?.category);
  const isBlocked = protocolRequired && !isProtocolDriven;

  /**
   * A device already booked in this window cannot take the appointment, so it is out of the running
   * before modality is even considered. isAvailable is undefined when the caller did not send a
   * window; treat that as available and let the save path guard.
   */
  const bookableDevices = useMemo(
    () => devices.filter((device) => device.isAvailable !== false),
    [devices],
  );

  const busyCount = devices.length - bookableDevices.length;

  const supportsModality = useCallback(
    (device: StaffDevice, category: DeviceCategory) =>
      getDeviceCapabilities(device.deviceConfig).includes(category),
    [],
  );

  /**
   * Everything offered in the treatment-type list: every modality the technician's rooms support,
   * plus the protocol's own modality even when unsupported, so the mismatch is visible instead of
   * being hidden by omission.
   */
  const modalityOptions = useMemo(() => {
    const fromDevices = bookableDevices.flatMap((device) => getDeviceCapabilities(device.deviceConfig));
    const fromProtocol = protocolTemplate?.category ? [protocolTemplate.category] : [];
    const all = new Set<DeviceCategory>([...fromProtocol, ...fromDevices]);

    // Keep the canonical order rather than discovery order.
    return DEVICE_CATEGORIES.map((c) => c.value).filter((value) => all.has(value));
  }, [bookableDevices, protocolTemplate]);

  const [modality, setModality] = useState<DeviceCategory>(
    initialDevice?.category ?? protocolTemplate?.category ?? modalityOptions[0] ?? "nf",
  );

  const [sessionConfig, setSessionConfig] = useState<DeviceConfig>(() => {
    // The protocol wins over whatever the appointment already carries: a montage saved before the
    // protocol changed is exactly what must not be booked again.
    if (protocolTemplate?.category) return normalizeDeviceConfig(protocolTemplate);
    if (initialDevice) return normalizeDeviceConfig(initialDevice);
    return normalizeDeviceConfig(getDefaultDeviceConfig(modalityOptions[0] ?? "nf"));
  });

  /** Only the rooms that can deliver the selected modality. */
  const matchingDevices = useMemo(
    () => bookableDevices.filter((device) => supportsModality(device, modality)),
    [bookableDevices, modality, supportsModality],
  );

  const [selectedDeviceId, setSelectedDeviceId] = useState<number>(
    initialDevice?.deviceContactId ?? 0,
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  // Keep the device selection consistent with the modality: drop it when the room cannot deliver
  // the selected treatment, and auto-pick when exactly the first matching room is available.
  useEffect(() => {
    setSelectedDeviceId((current) => {
      if (current && matchingDevices.some((d) => d.deviceContactId === current)) return current;
      return matchingDevices[0]?.deviceContactId ?? 0;
    });
  }, [matchingDevices]);

  useEffect(() => {
    if (!initialDevice?.deviceContactId) return;
    setSelectedDeviceId(initialDevice.deviceContactId);
    // Only the machine is restored under a protocol; the session itself stays the protocol's.
    if (isProtocolDriven) return;
    if (initialDevice.category) setModality(initialDevice.category);
    setSessionConfig(normalizeDeviceConfig(initialDevice));
  }, [initialDevice, isProtocolDriven]);

  const selectedDevice = matchingDevices.find((d) => d.deviceContactId === selectedDeviceId);

  const handleModalityChange = useCallback(
    (next: DeviceCategory) => {
      setModality(next);
      // Returning to the protocol's modality restores its montage; any other choice starts clean.
      setSessionConfig(
        protocolTemplate?.category === next
          ? normalizeDeviceConfig(protocolTemplate)
          : getDefaultDeviceConfig(next),
      );
      setValidationError(null);
    },
    [protocolTemplate],
  );

  const handleConfirm = () => {
    const configWithCategory = normalizeDeviceConfig({ ...sessionConfig, category: modality });
    const errorKey = validateDeviceConfig(configWithCategory);
    if (errorKey) {
      setValidationError(errorKey);
      return;
    }
    if (!selectedDevice) {
      setValidationError("DEVICE_NO_MATCHING_DEVICE");
      return;
    }
    onConfirm(staffDeviceToAppointmentDevice(selectedDevice, configWithCategory));
  };

  const hasMatchingDevice = matchingDevices.length > 0;

  return (
    <div
      className={modalStyles.modalOverlay}
      style={{ zIndex: 10002 }}
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className={`${modalStyles.modal} ${styles.modal}`} dir={isRtl ? "rtl" : "ltr"}>
        <div className={modalStyles.modalHeader}>
          <div className={modalStyles.modalTitle}>
            <span>🔌</span>
            <span>{t("DEVICE_SESSION_TITLE")}</span>
          </div>
          <button type="button" className={modalStyles.closeBtn} onClick={onCancel}>
            ×
          </button>
        </div>

        <div className={modalStyles.modalBody}>
          {/* Blocked is the whole screen, not a warning above a form: with no protocol there is no
              session to configure, and offering the fields would only invite a refused save. */}
          {isBlocked ? (
            <p className={styles.deviceMismatch}>{t("DEVICE_PROTOCOL_REQUIRED")}</p>
          ) : (
          <>
          <p className={styles.hint}>
            {isProtocolDriven ? t("DEVICE_SESSION_HINT_PROTOCOL") : t("DEVICE_SESSION_HINT")}
          </p>

          {isProtocolDriven && (
            <div className={modalStyles.formGroup}>
              <label className={modalStyles.label}>{t("DEVICE_SELECT_MODALITY")}</label>
              <div className={modalStyles.input} aria-readonly="true">
                {DEVICE_CATEGORY_DISPLAY[modality] ?? modality}
              </div>
            </div>
          )}

          {!isProtocolDriven && modalityOptions.length > 0 && (
            <div className={modalStyles.formGroup}>
              <label className={modalStyles.label}>{t("DEVICE_SELECT_MODALITY")}</label>
              <select
                className={modalStyles.input}
                value={modality}
                onChange={(e) => handleModalityChange(e.target.value as DeviceCategory)}
              >
                {modalityOptions.map((cap) => {
                  const meta = DEVICE_CATEGORIES.find((c) => c.value === cap);
                  return (
                    <option key={cap} value={cap}>
                      {meta ? t(meta.labelKey) : cap}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          <div className={modalStyles.formGroup}>
            <label className={modalStyles.label}>
              <span className={modalStyles.labelIcon}>🔌</span>
              {t("SELECT_DEVICE")}
            </label>
            <select
              className={modalStyles.input}
              value={selectedDeviceId || ""}
              disabled={!hasMatchingDevice}
              onChange={(e) => setSelectedDeviceId(Number(e.target.value))}
            >
              {!hasMatchingDevice && <option value="">—</option>}
              {matchingDevices.map((d) => (
                <option key={d.deviceContactId} value={d.deviceContactId}>
                  {d.deviceName}
                </option>
              ))}
            </select>
            {/* Two very different reasons for an empty list — saying "no matching device" when the
                device exists but is booked would send the user hunting for the wrong problem. */}
            {!hasMatchingDevice && (
              <p className={styles.deviceMismatch}>
                {busyCount > 0 && bookableDevices.length === 0
                  ? t("DEVICE_ALL_BUSY_AT_TIME")
                  : t("DEVICE_NO_MATCHING_DEVICE")}
              </p>
            )}
          </div>

          {isProtocolDriven ? (
            <DeviceConfigSections config={sessionConfig} />
          ) : (
            <DeviceConfigForm
              value={sessionConfig}
              onChange={(next) => {
                setSessionConfig(next);
                setValidationError(null);
              }}
              fixedCategory={modality}
            />
          )}

          {validationError && <div className={styles.error}>{t(validationError)}</div>}
          </>
          )}
        </div>

        <div className={modalStyles.modalFooter}>
          <button type="button" className={modalStyles.cancelBtn} onClick={onCancel}>
            {t("CANCEL")}
          </button>
          <button
            type="button"
            className={modalStyles.saveBtn}
            onClick={handleConfirm}
            disabled={!hasMatchingDevice || isBlocked}
          >
            {t("CONFIRM")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeviceConfigurationModal;
